/**
 * Device-local layout chrome (sidebars, panel collapse, widths).
 * Never synced via appSettings — phone and desktop must not overwrite each other.
 */

export const LAYOUT_STORAGE_KEYS = {
  leftSidebarCollapsed: 'layout.leftSidebarCollapsed',
  rightSidebarCollapsed: 'layout.rightSidebarCollapsed',
  leftSidebarWidth: 'layout.leftSidebarWidth',
  rightSidebarWidth: 'layout.rightSidebarWidth',
  dashboardSidebarCollapsed: 'layout.dashboardSidebarCollapsed',
  mobileWatchlistsCollapsed: 'layout.mobileWatchlistsCollapsed',
  mobileChartCollapsed: 'layout.mobileChartCollapsed',
  mobileOrderSettingsCollapsed: 'layout.mobileOrderSettingsCollapsed',
  collapsibleTablesState: 'layout.collapsibleTablesState',
  backtestLeftWidth: 'layout.backtestLeftWidth',
  backtestRightWidth: 'layout.backtestRightWidth',
  backtestWorkspace: 'layout.backtestWorkspace',
};

/** Former localStorage keys — deleted on load; never read again. */
const LEGACY_LOCAL_KEYS = [
  'leftSidebarCollapsed',
  'rightSidebarCollapsed',
  'leftSidebarWidth',
  'sidebarWidth',
  'collapsibleTablesState',
];

/** Former appSettings fields — stripped on every persist; never hydrated. */
export const LEGACY_SERVER_LAYOUT_KEYS = [
  'mobileWatchlistsCollapsed',
  'mobileChartCollapsed',
  'mobileOrderSettingsCollapsed',
];

let legacyLocalCleared = false;

export function clearLegacyLayoutLocalStorage() {
  if (legacyLocalCleared || typeof localStorage === 'undefined') return;
  legacyLocalCleared = true;
  for (const key of LEGACY_LOCAL_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch (_) {}
  }
}

clearLegacyLayoutLocalStorage();

export function readLayoutBool(key, defaultValue = false) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return defaultValue;
    return JSON.parse(raw) === true;
  } catch (_) {
    return defaultValue;
  }
}

export function writeLayoutBool(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(!!value));
  } catch (_) {}
}

export function readLayoutInt(key, defaultValue, { min = 0, max = Number.POSITIVE_INFINITY } = {}) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return defaultValue;
    const v = parseInt(raw, 10);
    if (Number.isNaN(v)) return defaultValue;
    return Math.max(min, Math.min(max, v));
  } catch (_) {
    return defaultValue;
  }
}

export function writeLayoutInt(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch (_) {}
}

export function readLayoutJson(key, defaultValue = null) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return defaultValue;
    return JSON.parse(raw);
  } catch (_) {
    return defaultValue;
  }
}

export function writeLayoutJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
}

/** Remove layout chrome keys so they are not stored on the server. */
export function stripServerLayoutSettings(appSettings) {
  if (!appSettings || typeof appSettings !== 'object') return appSettings || {};
  const next = { ...appSettings };
  for (const key of LEGACY_SERVER_LAYOUT_KEYS) {
    delete next[key];
  }
  return next;
}
